<template>
  <div class="w-full lg:max-w-[430px] 2xl:max-w-[630px]">
    <div class="px-4 lg:px-0">
      <ClientOnly fallback-tag="span" fallback="Loading ...">
        <div
          class="tabs"
          :class="{
            'has-icon': hasIcon,
            'compact-tabs': isCompact,
            'equal-width-tabs': useEqualWidthTabs,
          }"
          :style="{
            '--tab-count': `${tabCount}`,
            '--active-tab': `${activeTab}`,
          }"
        >
          <div
            class="pointer-events-none absolute bottom-4 left-0 right-0 z-0 h-0.5 bg-blue-light"
          />
          <div
            v-if="useEqualWidthTabs"
            class="active-indicator pointer-events-none absolute bottom-4 left-0 z-10 h-0.5"
          />
          <div
            v-if="!atStart"
            class="absolute bottom-4 left-0 z-50 bg-gradient-to-l from-transparent via-white to-white lg:hidden"
          >
            <!-- Pointer only: the arrow keys switch tabs and scroll the strip to them. -->
            <button
              type="button"
              tabindex="-1"
              aria-hidden="true"
              class="py-4 pr-10 text-blue"
              @click="scrollByTab(-1)"
            >
              <ChevronRight class="rotate-180" />
            </button>
          </div>
          <div ref="strip" class="tabs-strip" role="tablist" @scroll.passive="updateEdges">
            <div
              v-for="(item, index) in tabs"
              :key="index"
              class="tabs-tab flex min-w-0 justify-center"
              :class="[
                useEqualWidthTabs ? 'w-full' : 'is-active-scroll-tab w-auto',
                !useEqualWidthTabs && index === activeTab ? 'is-active' : '',
                tabSpacing,
              ]"
            >
              <div
                :class="
                  twMerge(
                    'flex min-w-0 justify-center fill-black px-3 py-4 hover:fill-blue hover:text-blue lg:px-4',
                    useDarkMode ? 'text-white' : 'text-black',
                    useEqualWidthTabs ? 'w-full' : 'w-auto',
                  )
                "
              >
                <button
                  :id="`${uid}-tab-${index}`"
                  type="button"
                  role="tab"
                  :aria-selected="index === activeTab"
                  :aria-controls="`${uid}-panel-${index}`"
                  :aria-label="item.icon ? item.tabLabel : undefined"
                  :tabindex="index === activeTab ? 0 : -1"
                  class="relative flex w-full min-w-0 cursor-pointer items-center justify-center font-sans text-sm focus-visible:outline-offset-[-2px] lg:text-lg"
                  :class="[
                    index === activeTab ? 'fill-blue tracking-normal text-blue' : 'tracking-normal',
                    item.icon && 'min-w-10 justify-center',
                  ]"
                  @click="setActiveTab(index)"
                  @keydown="onTabKeydown($event, index, setActiveTab)"
                >
                  <span v-if="item.icon" class="size-5" :class="[item.iconClass]">
                    <component
                      :is="item.icon"
                      v-if="item.icon"
                      class="size-5"
                      :class="[item.iconClass]"
                    />
                  </span>
                  <span class="relative flex min-w-0" :class="[item.icon && 'ml-2']">
                    <span
                      :class="[
                        item.icon ? 'hidden lg:block' : 'block',
                        index === activeTab ? 'opacity-1' : 'opacity-0',
                      ]"
                      class="max-w-full truncate font-bold"
                    >
                      {{ item.tabLabel }}
                    </span>

                    <span
                      :class="[
                        item.icon ? 'hidden lg:block' : 'block',
                        index === activeTab ? 'opacity-0' : 'opacity-1',
                      ]"
                      class="absolute left-0 max-w-full truncate"
                      aria-hidden="true"
                    >
                      {{ item.tabLabel }}
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="!atEnd"
            class="absolute bottom-4 right-0 z-50 bg-gradient-to-r from-transparent via-white to-white lg:hidden"
          >
            <button
              type="button"
              tabindex="-1"
              aria-hidden="true"
              class="py-4 pl-10 text-blue"
              @click="scrollByTab(1)"
            >
              <ChevronRight />
            </button>
          </div>
        </div>
      </ClientOnly>
    </div>

    <slot name="content"></slot>
    <div class="px-4 lg:mt-6 lg:px-0">
      <ClientOnly fallback-tag="span" fallback="Loading ...">
        <TabPanels :active="activeTab" class="tabs-content">
          <div
            v-for="(item, index) in tabs"
            :id="`${uid}-panel-${index}`"
            :key="index"
            role="tabpanel"
            :aria-labelledby="`${uid}-tab-${index}`"
            tabindex="0"
          >
            <div :class="useDarkMode ? 'text-white' : ''">
              <h3 class="mb-4 font-sans text-base font-bold lg:text-xl">
                {{ item.title }}
              </h3>
              <div class="font-sans text-sm leading-normal lg:text-base" v-html="item.copy"></div>
              <!-- Render slot if available -->
              <slot :name="`tab-${index}`" v-bind="{ tab: item }"></slot>

              <LinkSecondary
                v-if="item?.link?.url"
                :link="item?.link?.url"
                :title="item?.link?.title"
                class="mt-8"
                type="blue"
                :external="item.link?.type === 'ext'"
              />
            </div>
          </div>
        </TabPanels>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, type Component, computed, inject, type Ref, watch, onBeforeUnmount, useId } from 'vue'

import LinkSecondary from '../atoms/LinkSecondary.vue'
import ChevronRight from '../atoms/Icons/ChevronRight.vue'
import TabPanels from './TabPanels.vue'
import { onTabKeydown } from '../../utils/tabKeys'

import type { ILinkData } from '../../types'
import { twMerge } from 'tailwind-merge'

export interface ITab {
  title?: string
  copy?: string
  link?: ILinkData
  tabLabel?: string
  icon?: Component
  iconClass?: string
}

interface Props {
  tabs?: ITab[]
}

const props = defineProps<Props>()
const settings = inject<Ref<string[]>>('settings', ref([]))
const useDarkMode = computed(() => settings.value?.includes('dark_theme') ?? false)

const uid = useId()
const activeTab = ref(0)
const strip = ref<HTMLElement>()
const atStart = ref(true)
const atEnd = ref(true)

const tabEls = () => Array.from(strip.value?.children ?? []) as HTMLElement[]

const updateEdges = () => {
  const el = strip.value
  if (!el) return
  atStart.value = el.scrollLeft <= 1
  atEnd.value = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1
}

// The strip is the tabs' offsetParent, so offsetLeft is a scroll position; the browser clamps
// the last few to the end, as Swiper did.
const scrollToTab = (tab: HTMLElement | undefined) =>
  tab &&
  strip.value?.scrollTo({
    left: tab.offsetLeft,
    behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
  })

const scrollByTab = (direction: 1 | -1) => {
  const left = strip.value?.scrollLeft ?? 0
  const tabs = tabEls()
  scrollToTab(
    direction === 1
      ? tabs.find(tab => tab.offsetLeft > left + 1)
      : tabs.reverse().find(tab => tab.offsetLeft < left - 1),
  )
}

const setActiveTab = (index: number) => {
  activeTab.value = index
  scrollToTab(tabEls()[index])
}

// The strip only exists once ClientOnly has mounted it. The tabs are observed too: a webfont
// swap widens them (and the overflow) without resizing the strip.
let resizeObserver: ResizeObserver | undefined
watch(
  [strip, () => props.tabs],
  ([el]) => {
    resizeObserver?.disconnect()
    if (!el || typeof ResizeObserver === 'undefined') return
    resizeObserver = new ResizeObserver(updateEdges)
    ;[el, ...tabEls()].forEach(target => resizeObserver?.observe(target))
  },
  { flush: 'post' },
)
onBeforeUnmount(() => resizeObserver?.disconnect())

const hasIcon = computed(() => props.tabs?.some(tab => tab.icon))
const isCompact = computed(() => (props.tabs?.length ?? 0) <= 2)
const tabCount = computed(() => props.tabs?.length ?? 1)
const useEqualWidthTabs = computed(() => tabCount.value <= 2)
// Swiper's spaceBetween: a right margin on every tab, the last one too.
const tabSpacing = computed(() =>
  useEqualWidthTabs.value ? '' : hasIcon.value ? 'mr-[14px] lg:mr-7' : 'mr-5 lg:mr-7',
)
</script>

<style>
.tabs {
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.tabs-strip {
  position: relative;
  z-index: 1;
  display: flex;
  width: 100%;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
}

.tabs-strip::-webkit-scrollbar {
  display: none;
}

.tabs .tabs-tab {
  position: relative;
  flex-shrink: 0;
  scroll-snap-align: start;
  width: auto;
}

.tabs.equal-width-tabs .tabs-tab {
  width: calc(100% / var(--tab-count)) !important;
}

.tabs .active-indicator {
  width: calc(100% / var(--tab-count));
  background: #00b4d2;
  transform: translateX(calc(var(--active-tab) * 100%));
  transition: transform 200ms ease;
}

.tabs .tabs-tab.is-active-scroll-tab.is-active {
  box-shadow: inset 0 -2px 0 0 #00b4d2;
}

@media (max-width: 1023px) {
  .has-icon .tabs-strip {
    justify-content: space-between;
  }
  .has-icon.compact-tabs .tabs-strip {
    justify-content: space-around;
  }
}

/* Global on purpose: act's GiftDelivery and MembershipTabsBlock reuse these class names. */
.tabs,
.tabs-content {
  padding-bottom: 1em !important;
}

.tabs .tabs-tab {
  z-index: 100;
}
</style>
